import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { AuthRequestCredentials } from "@trello-powerup-monorepo/iframe-messenger-paywall-v2/parent";
import { ENVS } from "@v2/shared/environment.config";
import { TrelloIframeClientManager } from "@v2/shared/api/trello";
import { Trello } from "types/my-trello-types";

export function useTrelloContext(options: {
  required: true;
}): Trello.PowerUp.Context;
export function useTrelloContext(options?: {
  required?: boolean;
}): Trello.PowerUp.Context | undefined;
export function useTrelloContext(options: { required?: boolean }) {
  const { required = true } = options ?? {};
  const client = useTrelloClient({ required: false });
  const context = useMemo(() => client?.getContext?.(), [client]);

  if (!context && required) {
    throw new Error("Failed to get Trello context. Client not initialized?");
  }

  return context;
}

export function useTrelloCredentials(): [
  AuthRequestCredentials | undefined,
  Dispatch<SetStateAction<AuthRequestCredentials | undefined>>,
] {
  const client = useTrelloClient();
  const [credentials, setCredentials] = useState<AuthRequestCredentials>();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!client || credentials || isLoading) return;

    setIsLoading(true);
    try {
      client
        .getRestApi()
        .getToken()
        .then((token) => {
          if (token) {
            setCredentials({ appKey: ENVS.appKey, token });
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("Failed to get Trello credentials:", error);
      setIsLoading(false);
    }
  }, [client, credentials, isLoading]);

  return [credentials, setCredentials];
}

function useTrelloClient(options?: { required?: boolean }) {
  const { required = false } = options ?? {};
  const client = useClientState();

  if (!client && required) {
    throw new Error("Failed to get Trello client. Client not initialized?");
  }

  return client;
}

function useClientState() {
  const [client, setClient] = useState(() =>
    TrelloIframeClientManager.getClient()
  );

  useEffect(() => {
    const unsubscribe = TrelloIframeClientManager.subscribeToClientUpdates(
      () => {
        setClient((current) => {
          const next = TrelloIframeClientManager.getClient();
          return current === next ? current : next;
        });
      }
    );

    setClient((current) => {
      const next = TrelloIframeClientManager.getClient();
      return current === next ? current : next;
    });

    return unsubscribe;
  }, []);

  return client;
}